import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowDown, CheckCircle, Star, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { serviceService, newsService, contentService } from '../services/api';
import { testimonials } from '../mockData';
import { useLanguage } from '../contexts/LanguageContext';

const Home = () => {
  const [services, setServices] = useState([]);
  const [news, setNews] = useState([]);
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const { getText, isEnglish } = useLanguage();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [servicesRes, newsRes, contentRes] = await Promise.all([
        serviceService.getAll(),
        newsService.getAll(4),
        contentService.get()
      ]);
      setServices(servicesRes.data);
      setNews(newsRes.data);
      setContent(contentRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Default features in case content is not loaded
  const defaultFeatures = [
    { icon: '👑', title: '優質', description: '優質女傭，專業配對' },
    { icon: '💝', title: '貼心', description: '優質服務，貼心跟進節省您的時間' },
    { icon: '✅', title: '完善', description: '完善售後，讓僱主放心、安心' },
    { icon: '⚡', title: '簡化', description: '簡化流程，用心配對貼心服務' }
  ];

  const defaultFeaturesEn = [
    { icon: '👑', title: 'Quality', description: 'Quality helpers, professional matching' },
    { icon: '💝', title: 'Caring', description: 'Quality service, caring follow-up to save your time' },
    { icon: '✅', title: 'Complete', description: 'Complete after-sales service for peace of mind' },
    { icon: '⚡', title: 'Simplified', description: 'Simplified process, dedicated matching service' }
  ];

  // Get features with correct language
  const getFeatures = () => {
    if (!content?.features || content.features.length === 0) {
      return isEnglish ? defaultFeaturesEn : defaultFeatures;
    }
    
    if (isEnglish) {
      // Use titleEn and descriptionEn from each feature object
      return content.features.map((feat, idx) => ({
        icon: feat.icon,
        title: feat.titleEn || defaultFeaturesEn[idx]?.title || feat.title,
        description: feat.descriptionEn || defaultFeaturesEn[idx]?.description || feat.description
      }));
    }
    return content.features;
  };

  const features = getFeatures();

  const steps = [
    { number: 1, title: '挑選女傭', titleEn: 'Select Helper', time: '約1-2星期', timeEn: 'About 1-2 weeks' },
    { number: 2, title: '簽署合約', titleEn: 'Sign Contract', time: '約2-3星期', timeEn: 'About 2-3 weeks' },
    { number: 3, title: '領事館公證合約', titleEn: 'Consulate Authentication', time: '約2星期', timeEn: 'About 2 weeks' },
    { number: 4, title: '入境處批核', titleEn: 'Immigration Approval', time: '約4-6星期', timeEn: 'About 4-6 weeks' },
    { number: 5, title: '領取簽證+安排到港', titleEn: 'Get Visa + Arrange Arrival', time: '菲1-2星期 / 印4-6星期', timeEn: 'PH 1-2 weeks / ID 4-6 weeks' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin" style={{ color: '#00b6a5' }} />
      </div>
    );
  }

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-teal-50 to-lime-50 py-20 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h1 className="text-5xl md:text-6xl font-bold leading-tight">
                <span style={{ color: '#00b6a5' }}>
                  {isEnglish 
                    ? (content?.heroTitleEn || 'Quality Domestic Helpers')
                    : (content?.heroTitle || '專業配對')}
                </span>
                <br />
                <span className="text-gray-900">
                  {isEnglish
                    ? (content?.heroSubtitleEn || 'Professional Matching')
                    : (content?.heroSubtitle || '用心跟進')}
                </span>
              </h1>
              <p className="text-xl text-gray-600">
                {isEnglish
                  ? (content?.heroDescriptionEn || 'Professional agency services for local and overseas Filipino and Indonesian domestic helpers. 24-hour hotline service, comprehensive care for all your needs.')
                  : (content?.heroDescription || '專業代辦本地、海外菲傭、印傭聘請服務。24小時專線服務，全面照顧客人所需及提供網上專業配對。')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/services">
                  <Button
                    size="lg"
                    className="text-lg px-8 py-6 transition-all duration-300 transform hover:scale-105"
                    style={{ backgroundColor: '#00b6a5' }}
                  >
                    {getText('查看服務', 'View Services')}
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <a
                  href={`https://api.whatsapp.com/send?phone=${(content?.phone || '+852 6187 1388').replace(/[^0-9]/g, '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button
                    size="lg"
                    variant="outline"
                    className="text-lg px-8 py-6 border-2 transition-all duration-300"
                    style={{ borderColor: '#00b6a5', color: '#00b6a5' }}
                  >
                    {getText('WhatsApp 查詢', 'WhatsApp Inquiry')}
                  </Button>
                </a>
              </div>
            </div>
            <div className="relative">
              <img
                src={content?.heroImage || "https://customer-assets.emergentagent.com/job_homecarer/artifacts/5mdoyb60_website%20photo%20V6.jpg"}
                alt="Professional Helper"
                className="rounded-2xl shadow-2xl w-full h-auto"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "https://customer-assets.emergentagent.com/job_homecarer/artifacts/5mdoyb60_website%20photo%20V6.jpg";
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="text-center border-2 hover:shadow-lg transition-shadow duration-300">
                <CardHeader>
                  <div className="text-5xl mb-4">{feature.icon}</div>
                  <CardTitle style={{ color: '#00b6a5' }}>{feature.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16" style={{ backgroundColor: '#f8fffe' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">
              <span style={{ color: '#00b6a5' }}>{getText('我們的服務', 'Our Services')}</span>
            </h2>
            <p className="text-xl text-gray-600">{getText('專業僱傭服務', 'Professional Employment Services')}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service) => (
              <Card key={service.id} className="hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
                <CardHeader>
                  <CardTitle className="text-xl" style={{ color: '#00b6a5' }}>
                    {isEnglish ? (service.titleEn || service.title) : service.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <p className="font-semibold text-gray-900">{getText('菲律賓女傭', 'Filipino Helper')}</p>
                    <p className="text-2xl font-bold" style={{ color: '#00b6a5' }}>
                      {service.filipinoPrice}
                    </p>
                  </div>
                  <div className="space-y-2">
                    <p className="font-semibold text-gray-900">{getText('印尼女傭', 'Indonesian Helper')}</p>
                    <p className="text-2xl font-bold" style={{ color: '#00b6a5' }}>
                      {service.indonesianPrice}
                    </p>
                  </div>
                  <div className="pt-4">
                    <p className="font-medium text-gray-700 mb-2">{getText('收費包括：', 'Price Includes:')}</p>
                    <ul className="space-y-1 text-sm text-gray-600">
                      {(isEnglish && service.includedEn?.length > 0 ? service.includedEn : service.included).slice(0, 3).map((item, idx) => (
                        <li key={idx} className="flex items-start">
                          <CheckCircle className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" style={{ color: '#00b6a5' }} />
                          <span>{item}</span>
                        </li>
                      ))}
                      <li className="text-gray-400">...</li>
                    </ul>
                  </div>
                  <Link to="/services">
                    <Button className="w-full mt-4" variant="outline" style={{ borderColor: '#00b6a5', color: '#00b6a5' }}>
                      {getText('查看詳情', 'View Details')}
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-8">
            <Link to="/services">
              <Button size="lg" style={{ backgroundColor: '#c8ff00', color: '#000' }}>
                {getText('查看所有服務', 'View All Services')}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Application Steps */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">
              <span style={{ color: '#00b6a5' }}>{getText('申請流程', 'Application Process')}</span>
            </h2>
          </div>

          {/* Desktop Layout */}
          <div className="hidden md:block relative">
            <div className="flex justify-between items-center">
              {steps.map((step, index) => (
                <React.Fragment key={step.number}>
                  <div className="flex flex-col items-center text-center flex-1">
                    <div
                      className="w-20 h-20 rounded-full flex items-center justify-center text-2xl font-bold text-white mb-4 shadow-lg"
                      style={{ backgroundColor: '#00b6a5' }}
                    >
                      {step.number}
                    </div>
                    <h3 className="text-lg font-semibold mb-2">{isEnglish ? step.titleEn : step.title}</h3>
                    <p className="text-sm text-gray-600">{isEnglish ? step.timeEn : step.time}</p>
                  </div>
                  {index < steps.length - 1 && (
                    <ArrowRight className="text-gray-300 h-8 w-8 flex-shrink-0 mx-2" />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Mobile Layout */}
          <div className="md:hidden">
            <div className="flex flex-col items-center space-y-2">
              {steps.map((step, index) => (
                <React.Fragment key={step.number}>
                  <div className="flex items-center w-full max-w-sm bg-gray-50 rounded-xl p-4 shadow-sm">
                    <div
                      className="w-14 h-14 rounded-full flex items-center justify-center text-xl font-bold text-white shadow-md flex-shrink-0"
                      style={{ backgroundColor: '#00b6a5' }}
                    >
                      {step.number}
                    </div>
                    <div className="ml-4 text-left">
                      <h3 className="text-base font-semibold">{isEnglish ? step.titleEn : step.title}</h3>
                      <p className="text-sm text-gray-600">{isEnglish ? step.timeEn : step.time}</p>
                    </div>
                  </div>
                  {index < steps.length - 1 && (
                    <div className="flex items-center justify-center py-1">
                      <ArrowDown className="h-6 w-6" style={{ color: '#00b6a5' }} />
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      {content?.showTestimonials !== false && (
        <section className="py-16" style={{ backgroundColor: '#f8fffe' }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold mb-4">
                <span style={{ color: '#00b6a5' }}>{getText('客戶見證', 'Client Testimonials')}</span>
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {testimonials.map((testimonial) => (
                <Card key={testimonial.id} className="hover:shadow-lg transition-shadow">
                  <CardContent className="pt-6">
                    <div className="flex mb-3">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} className="h-5 w-5 fill-current" style={{ color: '#c8ff00' }} />
                      ))}
                    </div>
                    <p className="text-gray-700 mb-4">{testimonial.comment}</p>
                    <div className="border-t pt-4">
                      <p className="font-semibold">{testimonial.name}</p>
                      <p className="text-sm text-gray-600">{testimonial.location}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Latest News */}
      {content?.showNews !== false && news.length > 0 && (
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center mb-12">
              <div>
                <h2 className="text-4xl font-bold">
                  <span style={{ color: '#00b6a5' }}>{getText('最新資訊', 'Latest News')}</span>
                </h2>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {news.map((item) => (
                <Card key={item.id} className="overflow-hidden hover:shadow-xl transition-shadow">
                  <img src={item.image} alt={item.title} className="w-full h-48 object-cover" />
                  <CardContent className="p-4">
                    <h3 className="font-semibold text-lg mb-2">{item.title}</h3>
                    <p className="text-sm text-gray-600 mb-2">{item.content.substring(0, 80)}...</p>
                    <p className="text-xs text-gray-400">{new Date(item.date).toLocaleDateString(isEnglish ? 'en-HK' : 'zh-HK')}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20" style={{ background: 'linear-gradient(135deg, #00b6a5 0%, #00d4bd 100%)' }}>
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-white mb-6">
            {getText('準備好聘請優質女傭？', 'Ready to Hire a Quality Helper?')}
          </h2>
          <p className="text-xl text-white mb-8">
            {getText('24小時客戶支援熱線', '24-Hour Customer Support Hotline')}: {content?.phone || '+852 6187 1388'}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-white px-8 py-6 text-lg" style={{ color: '#00b6a5' }}>
                {getText('立即查詢', 'Inquire Now')}
              </Button>
            </Link>
            <a href={`https://api.whatsapp.com/send?phone=${(content?.phone || '+852 6187 1388').replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="px-8 py-6 text-lg" style={{ backgroundColor: '#c8ff00', color: '#000' }}>
                {getText('WhatsApp 聯絡', 'WhatsApp Contact')}
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
